import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Profile = {
  id: string;
  username: string;
  created_at: string;
};

export type ScamReport = {
  id: string;
  user_id: string | null;
  title: string;
  category: string;
  description: string;
  website_link: string;
  telegram_link: string;
  tiktok_link: string;
  facebook_link: string;
  image_urls: string[];
  source?: string;
  discovery_query?: string;
  created_at: string;
  updated_at: string;
};

export type ScammerReport = {
  id: string;
  user_id: string;
  scammer_name: string;
  real_name: string;
  title: string;
  category: string;
  description: string;
  website_link: string;
  telegram_link: string;
  tiktok_link: string;
  facebook_link: string;
  instagram_link: string;
  email: string;
  phone_numbers: string[];
  image_urls: string[];
  upvote_count: number;
  created_at: string;
  updated_at: string;
};

export type ScammerUpvote = {
  id: string;
  scammer_report_id: string;
  user_id: string;
  created_at: string;
};

export type ScammerComment = {
  id: string;
  scammer_report_id: string;
  user_id: string;
  comment_text: string;
  image_urls: string[];
  created_at: string;
  profiles?: { username: string };
};

export type ReviewRequest = {
  id: string;
  user_id: string;
  report_type: 'scam' | 'scammer';
  report_id: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string;
  reviewed_at: string | null;
  created_at: string;
};

export type AdminUser = {
  user_id: string;
  created_at: string;
};

export type Donation = {
  id: string;
  user_id: string;
  report_type: 'scam' | 'scammer';
  report_id: string;
  amount: number;
  payment_status: 'pending' | 'completed' | 'failed';
  payment_provider: string;
  transaction_id: string;
  created_at: string;
};

export type AIScamPost = {
  id: string;
  user_id: string;
  title: string;
  user_story: string;
  ai_analysis: string;
  confidence_level: number;
  scam_scenario: string;
  why_scam: string;
  how_to_avoid: string;
  view_count: number;
  created_at: string;
};

export type ScamCheckPost = {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  post_type: 'legacy' | 'scenario' | 'domain';
  category: string;
  slug: string;
  score: number | null;
  verdict: string | null;
  verdict_label: string | null;
  published: boolean;
  published_at: string | null;
  created_at: string;
  sources: string[];
  view_count: number;
  tags: string[];
};

export type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

export type BlogPost = {
  id: string;
  slug: string;
  h1: string;
  meta_title: string;
  meta_description: string;
  focus_keyword: string;
  secondary_keywords: string[];
  intro: string;
  sections: { h2: string; content: string }[];
  red_flags: string[];
  what_to_do: string;
  faq: { question: string; answer: string }[];
  sources?: string[];
  cta: string;
  target_regions: string[];
  cover_image_url?: string;
  topic: string;
  angle: string;
  topic_reasoning: string | null;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
};

export type DomainScan = {
  id: string;
  domain: string;
  slug: string;
  verdict: string;
  confidence_score: number;
  confidence_label: string;
  scam_category: string;
  one_line_verdict: string;
  red_flags: string[];
  green_flags: string[];
  seo_title: string;
  meta_description: string;
  article_html: string;
  schema_json_ld: string | null;
  sources?: string[];
  cover_image_url?: string;
  source?: string;
  discovery_query?: string;
  user_id: string | null;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
};

export type ReportPayment = {
  id: string;
  user_id: string;
  report_type: 'scam' | 'scammer';
  report_id: string | null;
  amount_kes: number;
  payment_status: 'pending' | 'completed' | 'failed';
  payment_provider: string;
  transaction_id: string | null;
  paystack_reference: string | null;
  customer_email: string;
  customer_phone: string | null;
  created_at: string;
  updated_at: string;
};

export type DomainInvestigation = {
  id: string;
  domain: string;
  slug: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  scamflag_score: number | null;
  verdict: 'safe' | 'proceed_with_caution' | 'likely_suspicious' | 'high_risk' | 'confirmed_scam' | null;
  search_identity: {
    fullDomain: string;
    rootName: string;
    segmentedPhrase: string;
    titleCasedBrand: string;
    allVariants: string[];
    isSubdomain: boolean;
    isFreeSubdomain: boolean;
  };
  report_html: string | null;
  evidence: Record<string, any> | null;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

export type DomainInvestigationTrack = {
  id: string;
  investigation_id: string;
  track_name: 'intelligence' | 'threat' | 'content' | 'verification' | 'reputation' | 'scoring';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at: string | null;
  completed_at: string | null;
  result: Record<string, any> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

export type ScenarioInvestigation = {
  id: string;
  slug: string;
  scam_type: string | null;
  scam_type_name?: string;
  scamflag_score: number | null;
  verdict: 'safe' | 'proceed_with_caution' | 'likely_suspicious' | 'high_risk' | 'confirmed_scam' | null;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  brand_name: string | null;
  domain: string | null;
  domain_investigation_id: string | null;
  scenario_description: string;
  phone_numbers: string[];
  email_addresses: string[];
  social_media_urls: string[];
  whatsapp_number: string | null;
  amount_involved: string | null;
  country: string | null;
  contact_method: string | null;
  source: string | null;
  investigation_results: Record<string, any> | null;
  report_html: string | null;
  seo_title: string | null;
  meta_description: string | null;
  schema_json_ld: string | null;
  sources: string[];
  published: boolean;
  published_at: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type ScenarioEvidenceUpdate = {
  id: string;
  scenario_investigation_id: string;
  submitted_by: string | null;
  additional_evidence: Record<string, any> | null;
  created_at: string;
};

export const SCAM_CATEGORIES = [
  'Forex',
  'Crypto',
  'Mobile Money',
  'Remote Jobs',
  'Jobs',
  'Online',
  'Offline',
  'Other',
] as const;

export function createSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function findReportBySlug(
  reports: (ScamReport | ScammerReport)[],
  slug: string
): { id: string; type: 'scam' | 'scammer' } | null {
  for (const report of reports) {
    if (createSlug(report.title) === slug) {
      return {
        id: report.id,
        type: (report as any).scammer_name ? 'scammer' : 'scam',
      };
    }
  }
  return null;
}
